import moment from "moment-timezone";

const hasValue = (value) =>
  value !== undefined && value !== null && value !== "";

const ready = (type, endpoint, payload) => ({
  outcome: "ready",
  type,
  endpoint,
  method: "post",
  payload,
});

const invalid = (type, code, message, fields = []) => ({
  outcome: "invalid",
  type,
  reason: {
    code,
    message,
    ...(fields.length ? { fields } : {}),
  },
});

const needsForm = (type, code, message, details = {}) => ({
  outcome: "needs_form",
  type,
  reason: { code, message, ...details },
});

const valueOr = (value, fallback) => (hasValue(value) ? value : fallback);
const asArray = (value) => (Array.isArray(value) ? value : []);
const DAY_ALIASES = {
  السبت: "sat",
  الأحد: "sun",
  الاحد: "sun",
  الإثنين: "mon",
  الاثنين: "mon",
  الثلاثاء: "tue",
  الأربعاء: "wed",
  الاربعاء: "wed",
  الخميس: "thu",
  الجمعة: "fri",
};
const lookupId = (value) =>
  value && typeof value === "object"
    ? value.value !== undefined
      ? value.value
      : value.id
    : value;

const configuredTimezone = (context) =>
  context.timezone || context.timeZone || context.gymTimezone;

const atTimezone = (value, context) => {
  const timezone = configuredTimezone(context);
  return timezone ? moment.tz(value, timezone) : moment(value);
};

const calendarMoment = (date, time, context) => {
  const dateString = atTimezone(date, context).format("DD/MM/YYYY");
  const timezone = configuredTimezone(context);
  const input = `${dateString} ${time}`;
  return timezone
    ? moment.tz(input, "DD/MM/YYYY HH:mm", true, timezone)
    : moment(input, "DD/MM/YYYY HH:mm", true);
};

const validateTimeRanges = (ranges) => {
  if (!ranges.length) return ["timesArray"];
  const invalidRange = ranges.some(({ from, to }) => {
    const start = moment(from, "HH:mm", true);
    const end = moment(to, "HH:mm", true);
    return !start.isValid() || !end.isValid() || !start.isBefore(end);
  });
  if (invalidRange) return ["timesArray"];

  const overlaps = ranges.some((first, index) =>
    ranges.slice(index + 1).some(
      (second) => first.from < second.to && first.to > second.from
    )
  );
  return overlaps ? ["timesArray"] : [];
};

const normalizedDateRange = (fields, context) => {
  const start = atTimezone(fields.startDate, context);
  const end = atTimezone(fields.endDate, context);
  if (!start.isValid() || !end.isValid() || end.isBefore(start, "day")) {
    return null;
  }
  return { start, end };
};

const deriveClassDates = (fields, context) => {
  if (asArray(fields.classDates).length) {
    return fields.classDates.map((date) => Number(date));
  }

  const range = normalizedDateRange(fields, context);
  const repeatDays = asArray(fields.repeatDays);
  if (!range || !repeatDays.length) return [];

  const selected = new Set(
    repeatDays.map((day) => {
      const value = String(day).trim();
      return DAY_ALIASES[value] || value.slice(0, 3).toLowerCase();
    })
  );
  const dates = [];
  const cursor = range.start.clone().startOf("day");
  const end = range.end.clone().startOf("day");
  while (!cursor.isAfter(end, "day")) {
    if (selected.has(cursor.format("ddd").toLowerCase())) {
      dates.push(cursor.valueOf());
    }
    cursor.add(1, "day");
  }
  return dates;
};

// This intentionally mirrors getClassDatesObject, including its existing
// hour-cancellation calculation, so Voice and the manual form send identical
// booking/session timestamps.
const addBookingWindows = (fields, sessionTimes) => {
  const start = fields.bookingWindowStart;
  const end = fields.bookingWindowEnd;
  const startUnit = valueOr(fields.bookingStartUnit, "d");
  const endUnit = valueOr(fields.bookingEndUnit, "d");
  const cancelBefore = valueOr(fields.cancelBefore, "1");
  const cancelUnit = valueOr(fields.cancelationWindowUnit, "h");

  const dates = sessionTimes.map((classTimes) => ({
    classTimes,
    lastCancelationTime:
      cancelUnit === "h"
        ? Number(
            moment(classTimes.from)
              .subtract(`0${start}:00:00`)
              .format("x")
          )
        : Number(
            moment(classTimes.from)
              .subtract(`00:0${cancelBefore}:00`)
              .format("x")
          ),
    bookingStart: !start
      ? 0
      : Number(
          (startUnit === "hours"
            ? moment(classTimes.from).subtract(`0${start}:00:00`)
            : moment(classTimes.from).subtract(start, startUnit)
          ).format("x")
        ),
    bookingEnd: !end
      ? 0
      : Number(
          (endUnit === "hours"
            ? moment(classTimes.from).subtract(`0${end}:00:00`)
            : moment(classTimes.to).subtract(end, endUnit)
          ).format("x")
        ),
  }));

  const conflict = dates.some(
    ({ bookingStart, bookingEnd, classTimes }) =>
      bookingStart &&
      bookingEnd &&
      (bookingStart >= bookingEnd ||
        bookingStart >= classTimes.from ||
        bookingEnd > classTimes.from + 1)
  );
  return conflict ? null : dates;
};

const sessionsForDates = (dates, ranges, context) =>
  dates.reduce((sessions, date) => {
    ranges.forEach(({ from, to }) => {
      sessions.push({
        from: Number(calendarMoment(date, from, context).format("x")),
        to: Number(calendarMoment(date, to, context).format("x")),
      });
    });
    return sessions;
  }, []);

const packagePricingEnabled = (context) => {
  const config = context.gymConfig || context.config || {};
  const appointments =
    config.manageMemberships &&
    config.manageMemberships.appointmentMemberships;
  return Boolean(
    appointments &&
      appointments.enabled &&
      appointments.ptGroupPricing &&
      appointments.ptGroupPricing.enabled
  );
};

const expandStaticTiers = (fields) => {
  const capacity = Number(fields.capacity);
  const price = Number(fields.price);
  const extra = Number(fields.extraPersonFee);
  return Array.from({ length: capacity }, (_, index) => ({
    players: index + 1,
    price: price + index * extra,
  }));
};

const ptCapacity = (fields) => {
  const capacity = Number(fields && fields.capacity);
  if (!Number.isFinite(capacity) || capacity <= 1) return 1;
  const hasGroupPricing =
    fields.pricingType === "static" ||
    fields.pricingType === "tiered" ||
    asArray(fields.tiers).length > 0;
  return hasGroupPricing ? capacity : 1;
};

const validatePackagePricing = (fields, enabled) => {
  const capacity = ptCapacity(fields);
  if (!enabled || fields.memberShipType !== "pt" || capacity <= 1) {
    return [];
  }
  if (fields.pricingType === "static") {
    return Number(fields.extraPersonFee) > 0 ? [] : ["extraPersonFee"];
  }
  if (fields.pricingType !== "tiered") return ["pricingType"];
  const tiers = asArray(fields.tiers);
  if (
    tiers.length !== capacity ||
    tiers.some((tier) => !tier || Number(tier.price) <= 0) ||
    Number(tiers[0] && tiers[0].price) !== Number(fields.price)
  ) {
    return ["tiers"];
  }
  return [];
};

const validatePeakPrices = (fields, enabled) => {
  if (
    !enabled ||
    fields.memberShipType !== "court" ||
    !fields.hasPeakOffPeakPricing
  ) {
    return [];
  }
  const intervals = asArray(fields.pricingIntervals);
  if (!intervals.length) return ["pricingIntervals"];
  const toMinutes = (time) => {
    const match = /^(\d{2}):(\d{2})$/.exec(time || "");
    return match ? Number(match[1]) * 60 + Number(match[2]) : NaN;
  };
  const ranges = [];
  for (const interval of intervals) {
    const start = toMinutes(interval.startTime);
    const end = toMinutes(interval.endTime);
    if (
      !["PEAK", "OFF_PEAK"].includes(interval.pricingType) ||
      !Number.isFinite(start) ||
      !Number.isFinite(end) ||
      start === end ||
      Number(interval.price) <= 0
    ) {
      return ["pricingIntervals"];
    }
    ranges.push(...(start < end ? [[start, end]] : [[start, 1440], [0, end]]));
  }
  const overlaps = ranges.some((first, index) =>
    ranges
      .slice(index + 1)
      .some((second) => first[0] < second[1] && first[1] > second[0])
  );
  return overlaps ? ["pricingIntervals"] : [];
};

export const preparePackagePayload = (fields = {}, context = {}) => {
  const missing = ["title", "session", "duration", "days", "memberShipType"].filter(
    (key) => !hasValue(fields[key])
  );
  const pricingEnabled = packagePricingEnabled(context);
  const peakPricing =
    pricingEnabled &&
    fields.memberShipType === "court" &&
    Boolean(fields.hasPeakOffPeakPricing);
  if (!peakPricing && !hasValue(fields.price)) missing.push("price");
  if (missing.length) {
    return invalid(
      "package",
      "invalid_package_payload",
      "The package draft does not satisfy the manual package form.",
      missing
    );
  }
  if (Number(fields.days) <= 0) {
    return invalid("package", "invalid_package_payload", "Duration must be positive.", [
      "days",
    ]);
  }
  if (
    fields.isSharedPackage &&
    fields.memberShipType === "GYM" &&
    !asArray(fields.sharedBranches).length
  ) {
    return invalid(
      "package",
      "invalid_package_payload",
      "Select at least one branch to share with.",
      ["sharedBranches"]
    );
  }
  const pricingErrors = [
    ...validatePackagePricing(fields, pricingEnabled),
    ...validatePeakPrices(fields, pricingEnabled),
  ];
  if (pricingErrors.length) {
    return invalid(
      "package",
      "invalid_package_pricing",
      "The package pricing configuration is invalid.",
      pricingErrors
    );
  }

  const courts = asArray(fields.courts).map(lookupId);
  if (courts.some((id) => !hasValue(id))) {
    return needsForm(
      "package",
      "incomplete_court_lookup",
      "A selected court could not be resolved to an ID.",
      { fields: ["courts"] }
    );
  }

  let tiers = asArray(fields.tiers);
  const capacity = ptCapacity(fields);
  if (
    pricingEnabled &&
    fields.memberShipType === "pt" &&
    capacity > 1 &&
    fields.pricingType === "static"
  ) {
    tiers = expandStaticTiers({ ...fields, capacity });
  }
  const durationCount =
    fields.duration === "M" ? Number(fields.days) * 30 : fields.days;
  const payload = {
    name: fields.title,
    sessionCount: String(fields.session),
    planType: fields.memberShipType,
    price: peakPricing ? 0 : fields.price,
    type: valueOr(fields.mode, "OFFLINE"),
    durationType: fields.duration,
    durationCount,
    hasClassessAccess: valueOr(fields.hasClassessAccess, false),
    allowTransfereBookings: valueOr(fields.allowTransfereBookings, false),
    courts,
    sharedBetweenFamily: valueOr(fields.sharedBetweenFamily, false),
    isforTrainerOnly: valueOr(fields.isforTrainerOnly, false),
    gender: valueOr(fields.gender, ""),
    requiredAttachment: valueOr(fields.requiredAttachment, false),
    sharedBranches: fields.isSharedPackage ? asArray(fields.sharedBranches) : [],
    poolAccessType: valueOr(fields.poolAccessType, null),
    capacity:
      fields.memberShipType === "pt"
        ? capacity
        : valueOr(fields.capacity, ""),
    count: String(fields.session),
    courtDurations: asArray(fields.courtDurations),
    courtUsageUnit:
      fields.memberShipType === "court"
        ? valueOr(fields.courtUsageUnit, "SESSION")
        : "SESSION",
    disableOnlineBooking: valueOr(fields.disableOnlineBooking, false),
    waitingList: valueOr(fields.waitingList, ""),
    waitingListCapacity: valueOr(fields.waitingListCapacity, ""),
    waitingListRule: valueOr(fields.waitingListRule, "auto"),
    freeTrial: valueOr(fields.freeTrial, false),
    allowCancellation: valueOr(fields.allowCancellation, true),
    allowUnPaidBooking: valueOr(fields.allowUnPaidBooking, false),
    cancelationWindowUnit: valueOr(fields.cancelationWindowUnit, "d"),
    cancelBefore: valueOr(fields.cancelBefore, ""),
    lastCancellationTimeInSeconds:
      parseInt(fields.cancelBefore, 10) *
        (valueOr(fields.cancelationWindowUnit, "d") === "d" ? 86400 : 3600) ||
      0,
    supportInsurance: valueOr(fields.supportInsurance, false),
    isActive: true,
  };
  if (pricingEnabled) {
    payload.tiers = tiers;
    payload.hasGroupPricing = Boolean(tiers.length);
    payload.hasPeakOffPeakPricing = peakPricing;
    payload.peakOffPeakPrices = peakPricing
      ? asArray(fields.pricingIntervals).map((interval) => ({
          pricingType: interval.pricingType,
          startTime: interval.startTime,
          endTime: interval.endTime,
          price: Number(interval.price),
        }))
      : [];
  }
  return ready("package", "package", payload);
};

const enrichAssignedPackages = (assigned, context) => {
  const available = asArray(context.packages || context.packageOptions);
  return assigned.map((item) => {
    const id = lookupId(item);
    const source =
      item && hasValue(item.sessionCount)
        ? item
        : available.find((candidate) => String(candidate.id) === String(id));
    if (!source) return null;
    const classes = asArray(source.classes);
    const usedSessions = hasValue(source.usedSessions)
      ? Number(source.usedSessions)
      : classes.reduce((sum, entry) => sum + Number(entry.count || 0), 0);
    const sessionCount = source.sessionCount;
    const remaining =
      sessionCount === "unlimited"
        ? sessionCount
        : Number(sessionCount) - usedSessions;
    const requestedCount = item && item.count;
    return {
      id,
      name: valueOr(item && item.name, source.name),
      count: parseInt(requestedCount, 10)
        ? requestedCount
        : source.allowTransfereBookings
        ? 0
        : sessionCount,
      planType: source.planType,
      sessionCount,
      usedSessions,
      remainingSessions: valueOr(source.remainingSessions, remaining),
      allowTransfereBookings: source.allowTransfereBookings,
    };
  });
};

const resolvedTrainer = (fields, context) => {
  const selected = context.selectedTrainer || context.trainer;
  if (
    selected &&
    (!hasValue(fields.trainer) || String(selected.id) === String(lookupId(fields.trainer)))
  ) {
    return selected;
  }
  return asArray(context.trainers).find(
    (trainer) => String(trainer.id) === String(lookupId(fields.trainer))
  );
};

const timeWithin = (time, shift) => time >= shift.start && time <= shift.end;

const hasTrainerScheduleConflict = (dates, ranges, scheduleDays, context) =>
  dates.some((date) => {
    const day = atTimezone(date, context).format("dddd");
    const shifts = asArray(scheduleDays && scheduleDays[day]);
    return (
      !shifts.length ||
      ranges.some(
        (range) =>
          !shifts.some(
            (shift) =>
              timeWithin(range.from, shift) && timeWithin(range.to, shift)
          )
      )
    );
  });

const prepareScheduledPayload = (type, fields, context, checkSchedule) => {
  const required = ["name", "capacity", "startDate", "endDate", "timesArray"];
  if (type === "workshop") {
    required.push(
      "trainer",
      "repeatDays",
      "groupType",
      "packageName",
      "packagePrice",
      "numberOfSessions",
      "packageDuration"
    );
  } else {
    required.push("repeatDays", "type", "cancelBefore");
  }
  const missing = required.filter((key) => {
    const value = fields[key];
    return !hasValue(value) || (Array.isArray(value) && !value.length);
  });
  if (missing.length || Number(fields.capacity) <= 0) {
    return invalid(
      type,
      `invalid_${type}_payload`,
      `The ${type} draft does not satisfy the manual form.`,
      missing.length ? missing : ["capacity"]
    );
  }
  const ranges = asArray(fields.timesArray);
  const rangeErrors = validateTimeRanges(ranges);
  const dateRange = normalizedDateRange(fields, context);
  if (rangeErrors.length || !dateRange) {
    return invalid(
      type,
      `invalid_${type}_schedule`,
      "The date or time schedule is invalid.",
      [...rangeErrors, ...(!dateRange ? ["startDate", "endDate"] : [])]
    );
  }
  const classDates = deriveClassDates(fields, context);
  if (!classDates.length) {
    return invalid(
      type,
      `invalid_${type}_schedule`,
      "The selected days do not produce any sessions in the date range.",
      ["repeatDays"]
    );
  }

  if (checkSchedule && hasValue(fields.trainer)) {
    const trainer = resolvedTrainer(fields, context);
    if (!trainer || !trainer.scheduleDays) {
      return needsForm(
        type,
        "trainer_schedule_lookup_required",
        "The selected trainer's schedule must be loaded before this class can be created.",
        { lookup: "trainer", id: lookupId(fields.trainer) }
      );
    }
    if (
      hasTrainerScheduleConflict(classDates, ranges, trainer.scheduleDays, context) &&
      !context.allowTrainerScheduleConflict &&
      !context.forceAdd
    ) {
      return needsForm(
        type,
        "trainer_schedule_conflict",
        "Class times are outside the trainer's schedule.",
        { lookup: "trainer", id: lookupId(fields.trainer), canForce: true }
      );
    }
  }

  const sessionTimes = sessionsForDates(classDates, ranges, context);
  const datesWithBookings = addBookingWindows(fields, sessionTimes);
  if (!datesWithBookings) {
    return invalid(
      type,
      "invalid_booking_window",
      "The booking window conflicts with the session time.",
      ["bookingWindowStart", "bookingWindowEnd"]
    );
  }
  if (
    type === "workshop" &&
    Number(fields.numberOfSessions) > datesWithBookings.length
  ) {
    return invalid(
      type,
      "session_count_exceeds_schedule",
      "The requested number of sessions exceeds the generated workshop sessions.",
      ["numberOfSessions"]
    );
  }

  const payload = {
    edit: false,
    payRates: null,
    waitingList: false,
    freeTrial: false,
    showTrainer: true,
    type: "OFFLINE",
    waitingListRule: type === "workshop" ? "" : "auto",
    waitingListCapacity: type === "workshop" ? 0 : undefined,
    autoCreateSinglePackage: false,
    allowCancelation: type === "workshop",
    allowUnpaidBooking: false,
    noShowDeduction: false,
    bookingWindowEnd: "",
    bookingWindowStart: "",
    bookingStartUnit: "d",
    bookingEndUnit: "d",
    cancelationWindowUnit: "h",
    cancelBefore: type === "workshop" ? "1" : fields.cancelBefore,
    gender: "",
    disableOnlineBooking: type === "workshop",
    court: null,
    ...(type === "class" ? { isPublished: true, description: "" } : {}),
    ...fields,
    trainer: lookupId(fields.trainer),
    classDates: datesWithBookings,
    startDate: dateRange.start.valueOf(),
    endDate: dateRange.end.valueOf(),
    payrateId:
      asArray(fields.payRates).length && fields.payRates[0]
        ? fields.payRates[0].id
        : false,
  };

  if (type === "workshop") {
    payload.assignedPackages = [];
  } else {
    const assigned = enrichAssignedPackages(
      asArray(fields.assignedPackages),
      context
    );
    if (assigned.some((item) => !item)) {
      return needsForm(
        type,
        "package_lookup_required",
        "Assigned package details must be loaded before this class can be created.",
        { lookup: "package", fields: ["assignedPackages"] }
      );
    }
    payload.assignedPackages = assigned;
  }
  return ready(type, "class/test", payload);
};

export const prepareClassPayload = (fields = {}, context = {}) =>
  prepareScheduledPayload("class", fields, context, true);

export const prepareWorkshopPayload = (fields = {}, context = {}) =>
  prepareScheduledPayload("workshop", fields, context, false);

export const packageCommand = { preparePayload: preparePackagePayload };
export const classCommand = { preparePayload: prepareClassPayload };
export const workshopCommand = { preparePayload: prepareWorkshopPayload };

export const scheduledCommands = {
  package: packageCommand,
  class: classCommand,
  workshop: workshopCommand,
};

export const getScheduledCommand = (type) => scheduledCommands[type];

export const preparePackageCreatePayload = preparePackagePayload;
export const prepareClassCreatePayload = prepareClassPayload;
export const prepareWorkshopCreatePayload = prepareWorkshopPayload;

export default scheduledCommands;
